class LocationVehicleModalManager {
  constructor() {
    this.modalInstances = {};
    this.isInitialized = false;
    this.storeSelectionCallback = null; // Callback to handle store selection without reload
  }

  init() {
    if (this.isInitialized) return;

    this.waitForModalStoreLocator();
    this.setupEventListeners();
    this.checkAutoPopup();
    this.isInitialized = true;
  }

  waitForModalStoreLocator() {
    if (typeof ModalStoreLocator !== 'undefined') {
      this.defineModalClass();
    } else {
      requestAnimationFrame(() => this.waitForModalStoreLocator());
    }
  }

  defineModalClass() {
    if (window.LocationVehicleStoreLocatorModal) return;

    // eslint-disable-next-line no-undef
    window.LocationVehicleStoreLocatorModal = class LocationVehicleStoreLocatorModal extends ModalStoreLocator {
      constructor(modalId = "location-vehicle-modal") {
        super();
        // Look for elements within the location vehicle modal container
        this.modalId = modalId;
        const modalContainer = document.querySelector(`#${modalId}`);
        this.storeLocatorEl = modalContainer?.querySelector("#store-locator");
        this.googleMapEl = modalContainer?.querySelector("#map");
        this.storeLocatorResultsEl = modalContainer?.querySelector("#store-results");
        this.storeLocatorSearchFormEl = modalContainer?.querySelector("#store-locator-search-form");
        this.storeLocatorSearchFormInputEl = modalContainer?.querySelector("#search-input");
        this.searchInputEl = modalContainer?.querySelector("#search-input");
        this.searchButtonEl = modalContainer?.querySelector("#search-button-mini");
        this.geoLocationBtnEl = modalContainer?.querySelector("#geoLocationBtn-mini");
        this.isModal = true;
        this.googleMapsLoaded = false;
        this.googleMapsLoading = false;
        this.isSearching = false;
        this._searchHandlers = null; // Store event handler references for cleanup
        this._hasSearched = false; // Track if a search has been performed
      }

      isMobile() {
        return window.innerWidth <= 768;
      }

      getViewportMeta() {
        return document.querySelector('meta[name="viewport"]');
      }

      preventZoomOnFocus() {
        if (!this.isMobile()) return;

        const viewport = this.getViewportMeta();
        if (!viewport) return;

        // Store original viewport content
        this._originalViewport = viewport.getAttribute('content');

        // Set viewport to prevent zoom
        const content = this._originalViewport || 'width=device-width,initial-scale=1';
        const updatedContent = content.includes('user-scalable=no')
          ? content
          : content + ',maximum-scale=1,user-scalable=no';

        viewport.setAttribute('content', updatedContent);
      }

      restoreViewportOnBlur() {
        if (!this.isMobile()) return;

        const viewport = this.getViewportMeta();
        if (!viewport || !this._originalViewport) return;

        // Restore original viewport content
        viewport.setAttribute('content', this._originalViewport);
        this._originalViewport = null;
      }

      async setupGoogleMaps() {
        if (this.isMobile()) {
          return;
        }

        if (!this.googleMapEl) {
          return;
        }

        await super.setupGoogleMaps();
      }

      async init() {
        await this.setupGoogleMaps();
        this.setupGeoLocationBtnEvents();
        await this.setupSearchEvents();
        await this.setupGoogleMarkers();
        this.renderStoreResults(this.locations || []);
        this.reCenterGoogleMap();
      }

      async setupGoogleMarkers() {
        if (this.isMobile() || !this.googleMap) {
          return;
        }

        await super.setupGoogleMarkers();
      }

      reCenterGoogleMap() {
        if (this.isMobile() || !this.googleMap) {
          return;
        }

        super.reCenterGoogleMap();
      }

      async setupSearchEvents() {
        if (!this.searchInputEl || !this.searchButtonEl) {
          return;
        }

        // Ensure search button doesn't submit the form by default
        if (this.searchButtonEl.type === "submit" || !this.searchButtonEl.type) {
          this.searchButtonEl.type = "button";
        }

        const currentValue = this.searchInputEl.value;
        if (!currentValue) {
          const query = new URLSearchParams(window.location.search).get("q") || "";
          this.searchInputEl.value = query;

          if (query.trim()) {
            await this.handleSearch(query);
          }
        }

        const onSearch = async () => {
          // Prevent duplicate API calls if search is already in progress
          if (this.isSearching) {
            return;
          }

          const value = this.searchInputEl.value?.trim() || "";
          // Only search if there's a meaningful search term
          if (value.length > 0) {
            await this.handleSearch(value);
          }
        };

        // Remove old listeners if they exist
        if (this._searchHandlers) {
          this.searchButtonEl.removeEventListener("click", this._searchHandlers.buttonClick);
          this.searchInputEl.removeEventListener("keydown", this._searchHandlers.inputKeydown);
          this.searchInputEl.removeEventListener("focus", this._searchHandlers.inputFocus);
          this.searchInputEl.removeEventListener("blur", this._searchHandlers.inputBlur);
          if (this.storeLocatorSearchFormEl) {
            this.storeLocatorSearchFormEl.removeEventListener("submit", this._searchHandlers.formSubmit);
          }
        }

        // Create new handler functions and store them as instance properties
        this._searchHandlers = {
          buttonClick: async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await onSearch();
          },
          inputKeydown: async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              await onSearch();
            }
          },
          inputFocus: () => {
            this.preventZoomOnFocus();
          },
          inputBlur: () => {
            // Delay restore to allow any click events to complete
            setTimeout(() => {
              this.restoreViewportOnBlur();
            }, 300);
          },
          formSubmit: async (e) => {
            // Always prevent form submission - we handle search explicitly via button click or Enter key only
            // This completely prevents any form submission, including accidental ones from clicking outside
            e.preventDefault();
            e.stopPropagation();
            // Do not call onSearch() here - search is only triggered by explicit button click or Enter key
            // This ensures clicking outside the input field never triggers a search
          }
        };

        // Add new listeners
        this.searchButtonEl.addEventListener("click", this._searchHandlers.buttonClick);
        this.searchInputEl.addEventListener("keydown", this._searchHandlers.inputKeydown);
        this.searchInputEl.addEventListener("focus", this._searchHandlers.inputFocus);
        this.searchInputEl.addEventListener("blur", this._searchHandlers.inputBlur);
        // Always add form submit handler to prevent any accidental form submissions
        if (this.storeLocatorSearchFormEl) {
          this.storeLocatorSearchFormEl.addEventListener("submit", this._searchHandlers.formSubmit);
        }
      }

      async handleSearch(term) {
        // Prevent duplicate API calls
        if (this.isSearching) {
          return;
        }

        // Mark that a search has been performed
        this._hasSearched = true;

        this.isSearching = true;
        this.setLoadingState(true);

        this.toggleMapContentMarginTop(true);

        try {
          const locations = await this.getLocations(term);
          this.locations = (locations || []).sort((a, b) => {
            if (typeof a.distance === "number" && typeof b.distance === "number") {
              return a.distance - b.distance;
            }
            return 0;
          });

          this.renderStoreResults(this.locations);
          await this.setupGoogleMarkers();
        } catch (e) {
          console.error("Error in search:", e);
          this.renderStoreResults([]);
          await this.setupGoogleMarkers();
        } finally {
          this.isSearching = false;
          this.setLoadingState(false);
          this.reCenterGoogleMap();
        }
      }

      toggleMapContentMarginTop(show) {
        const mapContent = document.querySelector('.location-vehicle-modal .map-content');
        if (!mapContent) return;
        mapContent.classList.toggle('add-margin-top', show);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.storeLocatorResultsEl.classList.add('is-loading');
          this.searchButtonEl.setAttribute('aria-disabled', 'true');
          this.searchInputEl.setAttribute('disabled', 'true');
        } else {
          this.storeLocatorResultsEl.classList.remove('is-loading');
          this.searchButtonEl.removeAttribute('aria-disabled');
          this.searchInputEl.removeAttribute('disabled');
        }
      }

      async getLocations(term = "") {
        try {
          const url = `${window.Titan?.utils?.API_ENDPOINT || '/api'}/locations${term ? `?term=${encodeURIComponent(term)}` : ""}`;
          const res = await fetch(url);
          const data = await res.json();
          this.searchCoordinates = data?.searchCoordinates || {};
          return data?.shopifyLocationRecords || [];
        } catch (e) {
          console.error("Failed to fetch locations:", e);
          return [];
        }
      }

      setupStoreCardClickEvents() {
        document.querySelectorAll(".store-card").forEach(card => {
          card.addEventListener("click", () => {
            this.clearActiveStoreCards();
            card.classList.add("active");

            if (this.isMobile() || !this.googleMap) {
              return;
            }

            const storeId = card.dataset.storeId;
            const loc = this.locations.find(
              l => (l.id || l.storeName?.replace(/\s+/g, "-").toLowerCase()) === storeId
            );

            if (!loc) return;

            const marker = this.googleMarkers.find(m =>
              m.getPosition().lat() === loc.geoLocation.latitude &&
              m.getPosition().lng() === loc.geoLocation.longitude
            );

            if (marker) {
              this.infoWindow.setContent(this.createInfoWindowContent(loc));
              this.infoWindow.open(this.googleMap, marker);
              this.googleMap.setCenter(marker.getPosition());
              this.googleMap.setZoom(12);
            }
          });
        });
      }

      formatVehicleModalHoursSuffix(closeText, state) {
        if (!closeText) return "";
        let s = closeText;
        if (state === "open" && /^Closes\s/i.test(s) && !/^Closes\s+at\s/i.test(s)) {
          s = s.replace(/^Closes\s+/i, "Closes at ");
        }
        return s.replace(/(\d)(AM|PM)\b/gi, "$1 $2");
      }

      buildMiniStoreCard(loc, index) {
        const storeId = loc.id || loc.storeName?.replace(/\s+/g, "-").toLowerCase() || "store";
        const isChecked = localStorage.getItem("selectedStoreId") === storeId;

        const card = document.createElement("div");
        card.className = "store-card store-card--full";
        card.dataset.index = index;
        card.dataset.storeId = storeId;

        const cardWrapper = document.createElement("div");
        cardWrapper.className = "store-card--wrapper";

        const cardWrapperDetails = document.createElement("div");
        cardWrapperDetails.className = "store-card--wrapper-details";


        const leftSection = document.createElement("div");
        leftSection.className = "store-card--left";

        const rightSection = document.createElement("div");
        rightSection.className = "store-card--right";

        const title = document.createElement("h4");
        title.className = "store-name";
        title.textContent = loc.storeName || "Store Location";

        let distance = document.createElement("div");
        distance.className = "store-distance";
        if (typeof loc.distance === "number" && !isNaN(loc.distance)) {
          distance.textContent = `${loc.distance.toFixed(1)} miles away`;
        }

        const addr1 = document.createElement("div");
        addr1.className = "store-address";
        addr1.textContent = `${loc.address1 || ""}, ${loc.city || ""}, ${loc.provinceCode || ""} ${loc.zipCode || ""}`;


        const storeHours = document.createElement("div");
        const holidayLabel =
          this.storeLocatorEl?.dataset.holidayLabel || "Holiday Hours";
        const holidayColor = this.storeLocatorEl?.dataset.holidayColor || "";
        const hrs = this.getStoreHoursText(loc, holidayLabel);
        storeHours.className = `store-hours store-hours--location-vehicle-modal store-hours--${hrs.state}`;

        if (hrs.holidayLabel) {
          const hlSpan = document.createElement("span");
          hlSpan.className = "store-hours__holiday-label";
          hlSpan.textContent = `${holidayLabel} - `;
          if (holidayColor) hlSpan.style.color = holidayColor;
          storeHours.append(hlSpan);
        }

        const openSpan = document.createElement("span");
        openSpan.className = "store-hours__open";
        openSpan.textContent = hrs.closeText
          ? `${hrs.openText} - `
          : hrs.openText;
        storeHours.append(openSpan);

        if (hrs.closeText) {
          const closeSpan = document.createElement("span");
          closeSpan.className = "store-hours__close";
          closeSpan.textContent = this.formatVehicleModalHoursSuffix(
            hrs.closeText,
            hrs.state
          );
          storeHours.append(closeSpan);
        }

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.id = "cb-location-vehicle-modal-" + storeId;
        checkbox.setAttribute("storeName", "choose-store");
        checkbox.setAttribute("data-store-id", storeId);
        checkbox.setAttribute("data-location", window.Titan?.utils?.encodeBase64(JSON.stringify(loc)) || btoa(JSON.stringify(loc)));
        if (isChecked) checkbox.checked = true;

        const label = document.createElement("label");
        label.setAttribute("for", "cb-location-vehicle-modal-" + storeId);
        label.textContent = "";

        const textSpan = document.createElement("span");
        textSpan.textContent = " Set as my store";

        const storeDetailsButton = document.createElement("button");
        storeDetailsButton.setAttribute('type', 'button');
        storeDetailsButton.className = "store-details-button";
        storeDetailsButton.textContent = "Store Details";
        storeDetailsButton.addEventListener("click", () => {
          window.open(loc.locationUrl, '_blank', 'noopener,noreferrer');
        });

        const selectStoreButton = document.createElement("button");
        selectStoreButton.setAttribute('type', 'button');
        selectStoreButton.className = "btn btn-primary";
        selectStoreButton.textContent = "Select Store";
        selectStoreButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          checkbox.checked = true;
          const changeEvent = new Event('change', { bubbles: true });
          checkbox.dispatchEvent(changeEvent);
        });


        const cbWrapper = document.createElement("div");
        cbWrapper.className = "store-checkbox";
        cbWrapper.append(checkbox, label, textSpan);

        leftSection.append(storeHours, addr1);
        rightSection.append(storeDetailsButton, selectStoreButton);
        cardWrapperDetails.append(leftSection, rightSection);

        cardWrapper.append(title, distance);
        cardWrapper.append(cardWrapperDetails);

        card.append(cardWrapper);

        checkbox.addEventListener("change", (e) => {
          this.handleStoreSelection(loc, e);
        });

        return card;
      }

      isLocationsPath() {
        const page = window.location.pathname.replace(/\/$/, "");
        return page.includes("/pages/locations");
      }

      handleStoreSelection(selectedStore, event) {
        const checkboxes = document.querySelectorAll(`#${this.modalId} input[storeName="choose-store"]`);
        checkboxes.forEach(cb => {
          if (cb !== event.target) cb.checked = false;
        });

        if (event.target.checked) {
          const storeId = selectedStore.id || selectedStore.storeName?.replace(/\s+/g, "-").toLowerCase() || "store";
          localStorage.setItem("selectedStoreId", storeId);

          if (selectedStore) {
            const formattedStoreData = window.Titan?.utils?.getFormattedStoreData(selectedStore);
            localStorage.setItem("selectedStoreData", JSON.stringify(formattedStoreData));
            window.Titan?.utils?.renderSelectedStoreInHeader(formattedStoreData);
          }

          if (window.locationVehicleModalManager?.storeSelectionCallback) {
            const formattedStoreData = window.Titan?.utils?.getFormattedStoreData(selectedStore);

            try {
              window.locationVehicleModalManager.storeSelectionCallback(formattedStoreData);
            } catch (error) {
              console.error('Error in store selection callback:', error);
            }

            window.locationVehicleModalManager.storeSelectionCallback = null;
            window.locationVehicleModalManager?.closeModal(this.modalId);
          } else {
            window.locationVehicleModalManager?.closeModal(this.modalId);
            const storePageUrl = window.Titan?.utils?.resolveStorePagePathOnCurrentOrigin(selectedStore?.locationUrl);
            if (this.isLocationsPath() && storePageUrl) {
              window.location.assign(storePageUrl);
            } else {
              window.location.reload();
            }
          }

          document.dispatchEvent(new CustomEvent("triggerCartStoreAttributeUpdate"));
        } else {
          localStorage.removeItem("selectedStoreId");
          localStorage.removeItem("selectedStoreData");
          window.Titan?.utils?.renderSelectedStoreInHeader(null);

        }
      }

      renderStoreResults(locations) {
        if (!this.storeLocatorResultsEl) return;

        this.storeLocatorResultsEl.innerHTML = "";

        // Only show "No stores found" if a search was actually performed
        // This prevents showing the message when user types but clicks outside without searching
        if (locations.length === 0 && this.searchInputEl.value.trim() !== "" && this._hasSearched) {
          const empty = document.createElement("p");
          empty.className = "no-stores-found";
          empty.textContent = "No stores found.";
          this.storeLocatorResultsEl.appendChild(empty);
          return;
        }


        locations.forEach((loc, index) => {
          const card = this.buildMiniStoreCard(loc, index);
          this.storeLocatorResultsEl.appendChild(card);
        });
      }
    };
  }


  setupEventListeners() {
    // FIX from Convermax: normalize click target for tabs (img/svg/span inside button)
    document.addEventListener('click', (e) => {
      const innerElement = e.target instanceof Element
        ? e.target.closest('.location-vehicle-tab *')
        : null;

      if (!innerElement) {
        return;
      }

      const tabButton = innerElement.closest('.location-vehicle-tab');

      if (!tabButton) {
        return;
      }

      if (e.target === tabButton) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      tabButton.click();
    }, true); // capture phase


    // Add click event listeners to open modal when clicking a trigger (element with class 'location-vehicle-modal').
    // Only run when the modal is closed so clicks inside the open modal (buttons, links, tabs) work normally.
    document.addEventListener('click', (e) => {
      if (!e.target.classList.contains('location-vehicle-modal') && !e.target.closest('.location-vehicle-modal')) {
        return;
      }

      const modal = document.getElementById('location-vehicle-modal');
      if (modal && modal.classList.contains('hidden')) {
        e.preventDefault();
        this.openModal('location-vehicle-modal');
      }
    });

    // Close modal when clicking on close button
    document.addEventListener('click', (e) => {
      if (e.target.id === 'close-store-locator-modal') {
        const locationModal = document.getElementById('location-vehicle-modal');

        if (locationModal && !locationModal.classList.contains('hidden')) {
          this.closeModal('location-vehicle-modal');
        }
      }

      // Scale animation when clicking overlay (instead of closing)
      if (e.target.classList.contains('modal-overlay')) {
        const modalParent = e.target.closest('.map-modal');
        if (modalParent) {
          const modalContent = modalParent.querySelector('.modal-content');
          if (modalContent) {
            // Add smooth scale animation
            modalContent.classList.add('smooth-scale');

            // Remove smooth-scale class after animation completes
            setTimeout(() => {
              modalContent.classList.remove('smooth-scale');
            }, 600);
          }
        }
      }
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const locationModal = document.getElementById('location-vehicle-modal');

        if (locationModal && !locationModal.classList.contains('hidden')) {
          this.closeModal('location-vehicle-modal');
        }
      }
    });

    document.addEventListener('locationVehicleModal:open', (e) => {
      const modalId = e.detail?.modalId || 'location-vehicle-modal';
      const activeTabId = e.detail?.activeTabId || null;

      if (window.innerWidth <= 568) {
        this.setupMobileConfiguration();
      } else {
        this.resetFindStoreButtonConfiguration();
      }

      if (activeTabId === 'tab-location' || activeTabId === 'tab-convermax') {
        this.switchToTab(modalId, activeTabId);
      }

      this.openModal(modalId);
    });

    document.addEventListener('locationVehicleModal:close', (e) => {
      const modalId = e.detail?.modalId || 'location-vehicle-modal';
      this.closeModal(modalId);
    });

    document.addEventListener('store-locator-modal', (e) => {
      const modalId = e.detail?.modalId || 'location-vehicle-modal';
      if (e.detail?.onStoreSelect) {
        this.storeSelectionCallback = e.detail.onStoreSelect;
      }
      this.openModal(modalId);
    });

    this.setupFindStoreButtonEvent();
  }

  setConvermaxTabVisibility(action) {
    const convermaxTab = document.querySelector('#tab-convermax');
    if (convermaxTab) {
      action === 'hide' ? convermaxTab.classList.add('hidden') : convermaxTab.classList.remove('hidden');
    }
  }

  setLocationPanelTitleVisibility(action) {
    const locationPanelTitle = document.querySelector('.location-vehicle-panel-title');
    if (locationPanelTitle) {
      action === 'hide' ? locationPanelTitle.classList.add('hidden') : locationPanelTitle.classList.remove('hidden');
    }
  }

  setConvermaxPanelTitleVisibility(action) {
    const convermaxPanelTitle = document.querySelector('.convermax-panel-title');
    if (convermaxPanelTitle) {
      action === 'hide' ? convermaxPanelTitle.classList.add('hidden') : convermaxPanelTitle.classList.remove('hidden');
    }
  }

  updateConvermaxPanelTitleText(text) {
    const convermaxPanelTitle = document.querySelector('#tab-convermax span');
    if (convermaxPanelTitle) {
      convermaxPanelTitle.textContent = text;
    }
  }

  getLocationModal() {
    return document.getElementById('location-vehicle-modal');
  }

  updateLocationTexts(modal, text) {
    const locationTab = modal.querySelector('#tab-location span');
    if (locationTab) {
      locationTab.textContent = text;
    }

    const locationPanelTitle = modal.querySelector('.location-vehicle-panel-title');
    if (locationPanelTitle) {
      locationPanelTitle.textContent = text;
    }
  }

  setFindStoreButtonConfiguration() {
    const modal = this.getLocationModal();
    if (!modal) return;

    this.switchToLocationTab();
    this.setConvermaxTabVisibility('hide');
    this.setLocationPanelTitleVisibility('hide');
    this.updateLocationTexts(modal, 'SELECT A STORE');
  }

  setupMobileConfiguration() {
    const modal = this.getLocationModal();
    if (!modal) return;

    this.setLocationPanelTitleVisibility('hide');
    this.updateLocationTexts(modal, 'SELECT A STORE');
    this.setConvermaxPanelTitleVisibility('hide');
    this.updateConvermaxPanelTitleText('ONE LAST STEP...');
  }

  resetFindStoreButtonConfiguration() {
    const modal = this.getLocationModal();
    if (!modal) return;

    this.setLocationPanelTitleVisibility('show');
    this.setConvermaxTabVisibility('show');
  }

  setupFindStoreButtonEvent() {
    const findStoreButtons = document.querySelectorAll('.find-store-btn') || [];
    const storeLocatorButtons = document.querySelectorAll('.store-locator-modal') || [];

    if (findStoreButtons.length > 0 || storeLocatorButtons.length > 0) {
      [...findStoreButtons, ...storeLocatorButtons].forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.setFindStoreButtonConfiguration();
          const modal = this.getLocationModal();
          if (modal) {
            modal.classList.add('find-store-triggered');
          }
          document.dispatchEvent(new CustomEvent('store-locator-modal'));
        });
      });
    }
  }

  openModal(modalId = 'location-vehicle-modal') {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    modal.classList.remove('hidden');

    if (typeof window.LocationVehicleStoreLocatorModal === 'undefined') {
      requestAnimationFrame(() => this.openModal(modalId));
      return;
    }

    // Initialize the new store locator modal functionality
    const instanceKey = `locationVehicleStoreLocatorModal_${modalId}`;
    if (!this.modalInstances[instanceKey]) {
      this.modalInstances[instanceKey] = new window.LocationVehicleStoreLocatorModal(modalId);
    }

    this.modalInstances[instanceKey]?.init();
  }

  closeModal(modalId = 'location-vehicle-modal') {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.add('hidden');
      modal.classList.remove('find-store-triggered');

      // Restore viewport if it was modified (prevent zoom cleanup)
      const instanceKey = `locationVehicleStoreLocatorModal_${modalId}`;
      const instance = this.modalInstances[instanceKey];
      if (instance && typeof instance.restoreViewportOnBlur === 'function') {
        instance.restoreViewportOnBlur();
      }
    }
  }

  switchToTab(modalId = 'location-vehicle-modal', tabId = 'tab-location') {
    const modal = document.getElementById(modalId);
    if (!modal) {
      return false;
    }

    const tabbedContent = modal.querySelector('tabbed-content');
    if (!tabbedContent) {
      return false;
    }

    const tab = modal.querySelector(`#${tabId}`);
    if (!tab) {
      return false;
    }

    if (typeof tabbedContent.activateTab === 'function') {
      tabbedContent.activateTab(tab);
      return true;
    } else {
      return false;
    }
  }

  switchToLocationTab(modalId = 'location-vehicle-modal') {
    return this.switchToTab(modalId, 'tab-location');
  }

  switchToConvermaxTab(modalId = 'location-vehicle-modal') {
    return this.switchToTab(modalId, 'tab-convermax');
  }

  checkAutoPopup() {
    if (!this.isProductOrCollectionOrSearchPage()) {
      return;
    }

    if (!this.hasStoreData()) {
      this.switchToLocationTab();

      document.dispatchEvent(new CustomEvent('locationVehicleModal:open', {
        detail: { modalId: 'location-vehicle-modal' }
      }));
      return;
    }
  }

  getCurrentPath() {
    return window.location.pathname;
  }

  hasStoreData() {
    const selectedStoreId = localStorage.getItem('selectedStoreId');
    const selectedStoreData = localStorage.getItem('selectedStoreData');
    return selectedStoreId && selectedStoreData ? true : false;
  }

  isProductOrCollectionOrSearchPage() {
    if (typeof window?.Shopify !== 'undefined' && window?.Shopify?.page) {
      return window.Shopify.page.type === 'product' || window.Shopify.page.type === 'collection' || window.Shopify.page.type === 'search';
    }

    const path = window.location.pathname;
    return path.includes('/products/') || path.includes('/collections/') || path.includes('/search');
  }

  hasLocationData() {
    const selectedStoreId = localStorage.getItem('selectedStoreId');
    const selectedStoreData = localStorage.getItem('selectedStoreData');

    return !!(selectedStoreId && selectedStoreData);
  }

  detectWindowOs() {
    if (navigator?.userAgent?.indexOf("Win") !== -1) {
      document.body.classList.add("is-windows");
    }
  }
}


document.addEventListener("DOMContentLoaded", function () {
  window.locationVehicleModalManager = new LocationVehicleModalManager();
  window.locationVehicleModalManager.detectWindowOs();
  window.locationVehicleModalManager.init();

  // as LocationVehicleModalManager loads after Convermax search.js, dispatch event that goes from Convermax
  document.dispatchEvent(new CustomEvent('locationVehicleModal:ready'));
});